import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { api } from "@courses-models/institution/src/ts/config";

/**
 * Describes data returned by v1/UnicUser/GetPerson from Gyldendal Access
 * Service.
 */
type v1_Unic_Institution_Teachers_Response = {
  Birthdate: string | null;
  Email: string | null;
  FirstName: string;
  Function: string | null;
  InstitutionId: string;
  LastName: string;
  Name: string;
  SchoolComName: string | null;
  UserId: string;
}[];

/**
 * Specification of the data format available within this collection.
 */
type Teacher = {
  id: string;
  name: string;
};

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<InstitutionTeacherCollection>({ ttl: 600 });

/**
 * Iterable collection of teachers available on the given institution.
 */
export class InstitutionTeacherCollection extends Collection<Teacher> {
  /**
   * Specifies the id of the institution that this collection is associated
   * with.
   */
  public readonly institutionId: string;

  /**
   * Contains an internal set containing all items available within this
   * collection.
   */
  protected readonly collection: Teacher[];

  /**
   * Cache collection of items when this collection is instantiated.
   */
  constructor(initial: { collection: Teacher[]; institutionId: string }) {
    super();

    this.collection = initial.collection;
    this.institutionId = initial.institutionId;
  }

  /**
   * Fetches a collection of courses that was shared with this student, and
   * converts each course into a well-typed model.
   */
  @nonConcurrent()
  public static async GET_BY_ID(
    institutionId: string
  ): Promise<InstitutionTeacherCollection> {
    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(institutionId)) {
      return <InstitutionTeacherCollection>cache.get(institutionId);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = `${api.gyldendalAccessService(
      "v1"
    )}/api/v1/Unic/Institution/Employees/${encodeURIComponent(institutionId)}`;

    const response = await axios.get<v1_Unic_Institution_Teachers_Response>(
      gGateway(endpoint)
    );

    // Format data into an array of class objects
    const models = response.data.map((item) => ({
      id: item.UserId,
      name:
        item.FirstName && item.LastName
          ? `${item.FirstName} ${item.LastName}`
          : item.Name,
    }));

    // Create our collection
    const collection = new InstitutionTeacherCollection({
      collection: models,
      institutionId,
    });

    // Cache the collection for future use
    cache.set(institutionId, collection);

    // Return the collection publicly
    return collection;
  }
}
